/**
 * SchedulingLinkModal — shows the generated scheduling URL for copy.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 */

import { useState, useRef, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';

export interface SchedulingLinkModalProps {
  tokenUrl: string;
  expiresAt: string;
  leadEmail: string;
  onClose: () => void;
}

export function SchedulingLinkModal({ tokenUrl, expiresAt, leadEmail, onClose }: SchedulingLinkModalProps) {
  const [copied, setCopied] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const dialog = dialogRef.current;
    if (dialog) {
      const first = dialog.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(tokenUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select the input
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scheduling-link-modal-title"
      data-testid="scheduling-link-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={dialogRef} className="bg-card rounded-lg border border-border p-6 max-w-lg w-full shadow-xl">
        <h3 id="scheduling-link-modal-title" className="text-base font-semibold text-foreground mb-1">
          Scheduling Link Generated
        </h3>
        <p className="text-sm text-muted-foreground mb-1">
          Share this link with the prospect so they can propose appointment times.
          It expires on{' '}
          <strong>{new Date(expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong>.
        </p>
        <p className="text-sm text-success mb-4">
          Sent to {leadEmail}
        </p>
        <div className="flex gap-2">
          <Input
            type="text"
            readOnly
            value={tokenUrl}
            className="flex-1 bg-muted/40 border-border px-3 py-2 font-mono text-foreground min-w-0 md:text-xs"
            onClick={(e) => (e.target as HTMLInputElement).select()}
            aria-label="Scheduling link URL"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="scheduling-link-copy-btn"
            onClick={() => void handleCopy()}
            className="shrink-0"
            aria-label={copied ? 'Link copied' : 'Copy scheduling link'}
          >
            {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
            <span className="ml-1">{copied ? 'Copied!' : 'Copy'}</span>
          </Button>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="scheduling-link-modal-close-btn"
            onClick={onClose}
            aria-label="Close scheduling link modal"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
